import { isRecord, toId, toText } from '../../shared/utils/parse';
import { ApiError } from '../api/api-error';
import type { AuthSession, AuthUser, CodeRequest } from '../models/auth/user.model';

/** Ответ не разобрался — это сбой контракта, а не ошибка пользователя: стор покажет его как сетевой. */
function malformed(): ApiError {
  return new ApiError(null, 'unknown');
}

/** `{phone, time, timestamp}` → таймер повтора считается от `requestedAtIso + ttlSec`. */
export function toCodeRequest(value: unknown): CodeRequest {
  if (!isRecord(value)) {
    throw malformed();
  }

  const { phone, time, timestamp } = value;
  const requestedAtIso = toText(timestamp);
  const ttlSec =
    typeof time === 'number' && Number.isFinite(time) ? Math.max(0, Math.trunc(time)) : null;
  // phone не храним, но без него ответ — битый контракт: проверку не убирать.
  if (toText(phone) === null || requestedAtIso === null || ttlSec === null) {
    throw malformed();
  }

  return { ttlSec, requestedAtIso };
}

/** Адрес уходит в `<img src>`: пускаем только http(s), чтобы чужая схема не попала в разметку. */
function asPhotoUrl(value: unknown): string | null {
  const text = toText(value);
  if (text === null) {
    return null;
  }
  return /^https?:\/\//i.test(text) ? text : null;
}

/** `client {id, phoneNumber, fullName, photoUrl}` → whitelist без лишних серверных полей. */
export function toAuthUser(value: unknown): AuthUser {
  if (!isRecord(value)) {
    throw malformed();
  }

  const { id, phoneNumber, fullName, photoUrl } = value;
  const stringId = toId(id);
  const phone = toText(phoneNumber);
  if (stringId === null || phone === null) {
    throw malformed();
  }

  return {
    id: stringId,
    name: toText(fullName),
    phone,
    photoUrl: asPhotoUrl(photoUrl),
    // Источника бонусов на бэкенде нет (docs/plan-api.md, Р-2).
    bonusBalance: 0,
  };
}

/** Ответ `/auth/verify`: `registered` не переносим — шаг с именем решает needsName по пустому имени. */
export function toAuthSession(value: unknown): AuthSession {
  if (!isRecord(value)) {
    throw malformed();
  }

  const { token, refreshToken, client } = value;
  const accessToken = toText(token);
  const nextRefreshToken = toText(refreshToken);
  if (accessToken === null || nextRefreshToken === null) {
    throw malformed();
  }

  return { token: accessToken, refreshToken: nextRefreshToken, user: toAuthUser(client) };
}

/** Ответ `/api/auth/refreshtoken`: серверный `accessToken` — это наш `token`. */
export function toTokenPair(value: unknown): { token: string; refreshToken: string } {
  if (!isRecord(value)) {
    throw malformed();
  }

  const { accessToken, refreshToken } = value;
  const token = toText(accessToken);
  const nextRefreshToken = toText(refreshToken);
  if (token === null || nextRefreshToken === null) {
    throw malformed();
  }

  return { token, refreshToken: nextRefreshToken };
}
